import { EQUIPMENT_LAYER_VERSION } from '../features/equipment/equipmentConstants';
import type {
  EquipmentDoc,
  EquipmentInstance,
  EquipmentTemplate,
} from '../features/equipment/equipmentTypes';

/** Equipment layer import/export helpers (explicit JSON files only). */

export function buildEquipmentDoc(
  templates: EquipmentTemplate[],
  instances: EquipmentInstance[],
  floorPlanName?: string | null
): EquipmentDoc {
  return {
    kind: 'equipment-layer',
    version: EQUIPMENT_LAYER_VERSION,
    exportedAt: new Date().toISOString(),
    floorPlanName: floorPlanName ?? null,
    templates,
    instances,
  };
}

export function isEquipmentDoc(value: unknown): value is EquipmentDoc {
  if (!value || typeof value !== 'object') return false;
  const doc = value as Partial<EquipmentDoc>;
  return (
    doc.kind === 'equipment-layer' &&
    Array.isArray(doc.templates) &&
    Array.isArray(doc.instances)
  );
}

export function exportEquipmentJson(doc: EquipmentDoc, filename = 'equipment'): void {
  const json = JSON.stringify(doc, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function importEquipmentJson(file: File): Promise<EquipmentDoc> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string) as unknown;
        if (!isEquipmentDoc(parsed)) {
          throw new Error('Not a valid equipment-layer document');
        }
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
}

const LEGACY_EQUIPMENT_STORAGE_KEY = 'layout_editor_equipment';

/** Remove cached equipment overlays from older builds (session-only now). */
export function clearLegacyEquipmentStorage(): void {
  try {
    localStorage.removeItem(LEGACY_EQUIPMENT_STORAGE_KEY);
  } catch (err) {
    console.error('Error clearing legacy equipment storage:', err);
  }
}
